import { inSample } from '../trace-utils';

/**
 * Configuração e GATES do Sentry.
 *
 * Funções puras, sem Angular e sem o SDK: é o que permite testar cada gate
 * isoladamente (é o teste do gate que impede uma conta de estourar) e é o que
 * mantém este arquivo no bundle EAGER sem custo — o SDK só entra pelo chunk
 * lazy, e só depois de todo gate aqui dizer sim.
 */

/** Bloco `sentry` dos environments. */
export interface SentryConfig {
  /**
   * DSN do projeto. **Vazio = desligado** — é o kill switch: sem DSN o chunk
   * do SDK nem é baixado. Um deploy com o campo vazio desliga o Sentry inteiro
   * sem remover código.
   */
  dsn: string;
  /** Tag `environment` no Sentry: separa production de green/blue. */
  environment: string;
  /**
   * % de DISPOSITIVOS que carregam o SDK (0-100). Alavanca mestre de custo E
   * de performance: quem está fora nem baixa o chunk. Determinística por
   * device (nunca por evento) — a mesma pessoa está sempre dentro ou sempre
   * fora, senão a sessão do Replay sai picotada e o funil de erro mente.
   */
  clientSamplePct: number;
  /** Fração de transações de performance amostradas (0-1). */
  tracesSampleRate: number;
  /** Fração de sessões gravadas SEM erro (0-1). Custo alto: default 0. */
  replaysSessionSampleRate: number;
  /** Fração de sessões gravadas QUANDO há erro (0-1). */
  replaysOnErrorSampleRate: number;
}

/** O que o app sabe sobre o ambiente na hora de decidir. */
export interface SentryRuntimeContext {
  production: boolean;
  hostname: string;
  isCypress: boolean;
  doNotTrack: boolean;
  deviceId: string;
}

/**
 * Motivo da decisão. Sai como string (e não boolean) de propósito: quando
 * alguém perguntar "por que não estou vendo erro nenhum no Sentry?", a resposta
 * é uma consulta, não uma investigação.
 */
export type MotivoBloqueio =
  | 'sem-dsn'
  | 'nao-producao'
  | 'localhost'
  | 'cypress'
  | 'do-not-track'
  | 'fora-da-amostra'
  | '';

export interface DecisaoCarga {
  carregar: boolean;
  motivo: MotivoBloqueio;
}

/** Hostnames de desenvolvimento — mesma regra do TraceTransportService. */
export function ehLocalhost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1'
    || hostname === '0.0.0.0' || /\.local$/.test(hostname);
}

/**
 * Carrega o SDK neste device?
 *
 * Ordem dos gates é do mais barato/definitivo para o mais caro, e o primeiro
 * que reprovar decide. `cypress` vem ANTES de qualquer coisa que dependa de
 * ambiente porque o E2E roda com `environment.production === false` em
 * localhost e mesmo assim precisa de um motivo próprio: sob Cypress o gate NÃO
 * é furado (ao contrário da telemetria própria, cujo endpoint é interceptável
 * — aqui o destino é o sentry.io de verdade, e um E2E não pode gastar quota
 * nem poluir o painel com erro sintético).
 */
export function decidirCarga(cfg: SentryConfig | undefined, ctx: SentryRuntimeContext): DecisaoCarga {
  if (!cfg || !cfg.dsn) return { carregar: false, motivo: 'sem-dsn' };
  if (ctx.isCypress) return { carregar: false, motivo: 'cypress' };
  if (!ctx.production) return { carregar: false, motivo: 'nao-producao' };
  if (ehLocalhost(ctx.hostname)) return { carregar: false, motivo: 'localhost' };
  // DNT: o Replay grava a tela da pessoa. Respeitar o sinal é barato e é o
  // mínimo defensável quando a captura vai além de contar erro.
  if (ctx.doNotTrack) return { carregar: false, motivo: 'do-not-track' };
  // Device SEM id fica fora, e o gate é explícito: `inSample('')` devolve
  // bucket 0, ou seja, um device sem id entraria em QUALQUER coorte ≥1% — o
  // oposto do pretendido, e concentrado justamente nos ambientes onde o storage
  // falha (modo privado, iframe). Sem id estável não há coorte estável.
  if (!ctx.deviceId) return { carregar: false, motivo: 'fora-da-amostra' };
  if (!inSample(ctx.deviceId, cfg.clientSamplePct)) return { carregar: false, motivo: 'fora-da-amostra' };
  return { carregar: true, motivo: '' };
}

/** Lê o sinal Do Not Track nas três grafias que os browsers já usaram. */
export function lerDoNotTrack(nav: any, win: any): boolean {
  try {
    const v = (nav && (nav.doNotTrack || nav.msDoNotTrack)) || (win && win.doNotTrack);
    return v === '1' || v === 'yes' || v === 1 || v === true;
  } catch (e) {
    return false;
  }
}

/**
 * Prefixos de ambiente que aparecem como PRIMEIRO segmento do path (os `baseHref`
 * do angular.json). Lista, e não uma cadeia de `||`, porque ela já foi esquecida
 * duas vezes: `blue` e `yellow` entraram depois, e um prefixo faltando aqui faz o
 * NOME DO AMBIENTE virar o slug — a tag do Sentry passa a dizer "yellow" para
 * toda loja.
 */
const PREFIXOS_DE_AMBIENTE = ['app', 'green', 'blue', 'yellow'];

/**
 * Slug do restaurante a partir do path (`/app/<slug>/...`, `/green/<slug>/...`
 * ou `/<slug>/...` em dev). Vira TAG no Sentry: sem ela, "erro subiu" não vira
 * "erro subiu NA LOJA X" — e é sempre por loja que o suporte pergunta.
 */
export function slugDoPath(pathname: string): string {
  try {
    const seg = String(pathname || '').split('/').filter(Boolean);
    const primeiro = seg[0];
    const slug = PREFIXOS_DE_AMBIENTE.indexOf(primeiro) !== -1 ? seg[1] : primeiro;
    if (!slug || slug.indexOf('.') > -1) return '';
    return /^[a-zA-Z0-9_-]+$/.test(slug) ? slug.slice(0, 60) : '';
  } catch (e) {
    return '';
  }
}
